import {
  Box,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@mui/material';
import { IEquipmentPrevOrder } from 'API';
import { CustomStepperIcon } from 'Components';
import { shorterDateFormat } from 'Config';
import { useIsDesktop } from 'Hooks';
import { Status } from 'Modules/Equipment/EquipmentMain/EquipmentRecentOrders/Status';
import { format } from 'date-fns';

interface EquipmentStepperProps {
  steps: IEquipmentPrevOrder['statuses'];
  currentStep: number;
}

const CustomStepMobile = styled(StepConnector)(() => ({
  marginLeft: '11.5px',
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#1752AF',
      minHeight: '40px',
    },
  },
  [`&.${stepConnectorClasses.disabled}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      minHeight: '40px',
    },
  },
}));

const CustomStepDesktop = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.disabled}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#E4C1FF',
      height: '48px',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#1752AF',
    },
  },
}));

const CustomConnector = () => {
  const isDesktop = useIsDesktop();
  return isDesktop ? <CustomStepDesktop /> : <CustomStepMobile />;
};

export const EquipmentOrderStepper = ({
  steps,
  currentStep,
}: EquipmentStepperProps) => {
  const isDesktop = useIsDesktop();

  return isDesktop ? (
    <Stepper
      alternativeLabel
      connector={<CustomConnector />}
      aria-hidden={'true'}
      orientation='vertical'
      sx={{ p: '24px', height: 'fit-content' }}
    >
      {steps.map((step) => {
        return (
          <Step
            key={step.mainStatus}
            completed={currentStep > step.mainStatus}
            sx={{
              width: '100%',
              '& .MuiStepConnector-root': { position: 'initial' },
            }}
            active={step.mainStatus === currentStep}
          >
            <StepLabel
              StepIconComponent={CustomStepperIcon}
              sx={{
                display: 'flex',
                '&.MuiStepLabel-alternativeLabel': {
                  flexDirection: 'row',
                  alignItems: 'start',
                },
                '& .MuiStepLabel-label': {
                  mt: 0,
                  textAlign: 'left',
                  ml: '12px',
                  height: step.mainStatusDate ? '60px' : '22px',
                },
              }}
            >
              {step.mainStatus === currentStep ? (
                <Status
                  status={step.mainStatus}
                  statusLabel={step.mainStatusLabel || ''}
                />
              ) : (
                <>
                  <Typography variant='body5'>
                    {step.mainStatusLabel}
                  </Typography>
                </>
              )}
              {!!step.mainStatusDate && (
                <Typography
                  variant='body5'
                  component={'p'}
                  sx={{ position: 'absolute', pt: '10px' }}
                >
                  {format(new Date(step.mainStatusDate), shorterDateFormat)}
                </Typography>
              )}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  ) : (
    <Stepper
      orientation='vertical'
      connector={<CustomConnector />}
      sx={{ marginTop: '16px', marginBottom: '48px' }}
    >
      {steps.map((step) => (
        <Step
          key={step.mainStatus}
          completed={currentStep > step.mainStatus}
          active={step.mainStatus === currentStep}
          sx={{ width: '100%' }}
        >
          <StepLabel
            StepIconComponent={CustomStepperIcon}
            sx={{
              position: 'relative',
              p: 0,
              top: step.mainStatus === currentStep ? '3px' : '',
              marginTop: step.mainStatus === currentStep ? '-7px' : '',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '-1.5px',
              }}
            >
              <Typography
                color={step.mainStatus === currentStep ? '#1752AF' : ''}
                fontWeight={step.mainStatus === currentStep ? 700 : ''}
              >
                {step.mainStatusLabel}
              </Typography>
            </Box>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};
